import { ChangeEvent, ComponentType, FocusEvent, KeyboardEvent, memo, useCallback, useState } from "react";
import classNames from "classnames";
import { Link } from "../../../ui/Link";

import { ReactComponent as CloseIcon } from "./icon/closeIcon.svg";

import styles from "./style.module.scss";

export type EmailInputProps = {
  onChange?: (value: string[]) => void,
  setInternalErrors?: (value: string) => void,
  value?: string[],
  hasErrors?: boolean,
  disabled?: boolean,
  autoFocus?: boolean,
  placeholder?: string,
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void,
}

export const EmailInput = memo<EmailInputProps>(({ onChange, value, hasErrors, setInternalErrors, onBlur, ...props }) => {
  const [ inputValue, setInputValue ] = useState<string>("");

  const isInList = useCallback((email: string) => {
    return value?.includes(email);
  }, [ value ]);

  const isEmail = useCallback((email: string) => {
    return /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(email);
  }, []);

  const isValid = useCallback((email: string) => {
    let error = null;

    if (isInList(email)) {
      error = `${email} has already been added.`;
    }

    if (!isEmail(email)) {
      error = `${email} is not a valid email address.`;
    }

    if (error) {
      setInternalErrors?.(error);
      return false;
    }

    return true;
  }, [ isInList, isEmail, setInternalErrors ]);


  const addNewEmail = useCallback((email: string) => {
    if (!!email && isValid(email)) {
      const newArray = [ ...value || [], email ];
      onChange?.(newArray);
      setInputValue("");
    }
  }, [ isValid, onChange, value ]);

  const handleKeyDown = useCallback((evt: KeyboardEvent<HTMLInputElement>) => {
    if ([ "Enter", " ", ",", ";" ].includes(evt.key)) {
      if (inputValue) {
        addNewEmail(inputValue);
        evt.preventDefault();
        evt.stopPropagation();
      }
    }
  }, [ inputValue, addNewEmail ]);

  const wrappedOnBlur = useCallback((e: FocusEvent<HTMLInputElement>) => {
    if (onBlur) {
      onBlur(e);
    }
    addNewEmail(e.target.value);
  }, [ onBlur, addNewEmail ]);

  const handleChange = useCallback((evt: ChangeEvent<HTMLInputElement>) => {
    setInputValue(evt.target.value.trim());
    setInternalErrors?.("");
  }, [ setInputValue, setInternalErrors ]);

  const handleDelete = useCallback((item: string) => {
    const newArray = value?.filter?.(i => i !== item);
    onChange?.(newArray || []);
  }, [ onChange, value ]);

  return (
    <div className={styles.root}>
      {!!value?.length && value?.map?.(item => (
        <div className={styles.tagItem} key={item}>
          {item}
          {!props?.disabled &&
            <Link
              tabIndex={-1}
              className={styles.cancel}
              linkType="l"
              component="button"
              onClick={() => handleDelete(item)}
            >
              <CloseIcon />
            </Link>}
        </div>
      ))}
      {!props?.disabled &&
        <div className={styles.inputBlock}>
          <input
            autoFocus={true}
            {...props}
            placeholder={(!!value?.length) ? "" : props?.placeholder}
            className={classNames({
              [styles.input]: true,
              [styles.hasError]: hasErrors,
            })}
            onKeyDown={handleKeyDown}
            onChange={handleChange}
            onBlur={wrappedOnBlur}
            value={inputValue}
          />
        </div>}
    </div>
  );

});


(EmailInput as ComponentType<EmailInputProps>).defaultProps = {};

export default EmailInput;
